// FLAGS: tenancy
// Banner offering an employer's referral-toolkit files, fetched for whatever employer id the caller
// passes in — it holds no check that the operator or the client belongs to that employer.
import { useEmployerReferralToolkitsQuery } from "@src/queries/employers";
import { apiPathsWithParams, USER_AVAILABLE_LANGUAGES } from "@src/constants";
import type { FC } from "react";
import Alert from "@openup/shared/components/Alert/Alert";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { EmployerReferralToolkitFile } from "@src/models/EmployerReferralToolkit";
import ButtonOrLink from "@openup/shared/components/ButtonOrLink/ButtonOrLink";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import {
  WithSnackbarProps,
  withSnackbar,
} from "@src/components/SnackBarComponent";

interface EmployerReferralToolkitBannerProps extends WithSnackbarProps {
  employerId: string;
}
const EmployerReferralToolkitBanner: FC<EmployerReferralToolkitBannerProps> = ({
  employerId,
  snackbarShowMessage,
}) => {
  const { t } = useTranslation();
  const { data: employerReferralToolkits } =
    useEmployerReferralToolkitsQuery(employerId);

  const getFileLable = (languageCode: string) => {
    return USER_AVAILABLE_LANGUAGES.find(
      (language) => language.value === languageCode,
    )?.label;
  };

  if (!employerReferralToolkits) return null;

  if (!employerReferralToolkits.files?.length)
    snackbarShowMessage(t("EmployerReferralToolkit.Error.NotFound"), "error");

  return (
    <Alert icon={<InfoOutlined />} variant="info">
      {t("EmployerReferralToolkit.Banner.Header")}

      <ul>
        <li>{employerReferralToolkits.description}</li>
        <li>
          {`${t("EmployerReferralToolkit.Banner.Title")}: `}
          {employerReferralToolkits.files?.map(
            (file: EmployerReferralToolkitFile) => (
              // FLAGS: tenancy
              // The employer id in this download URL is decorative: the API's file action binds only
              // the `fileName` query value and streams the matching blob, so the id cannot bound which
              // employer's file comes back. Do not add per-employer files on the strength of this path.
              <ButtonOrLink
                key={file.fileName}
                className="cursor-pointer !underline text-white decoration-white mr-2"
                href={`${getApiBaseUrl()}${apiPathsWithParams.downloadEmployerRefferalToolkit(
                  employerId,
                  file.fileName,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getFileLable(file.languageCode)}
              </ButtonOrLink>
            ),
          )}
        </li>
      </ul>
    </Alert>
  );
};

export default withSnackbar(EmployerReferralToolkitBanner);
